/**
 * useAccountLanguage — Langue de l'interface ↔ langue du compte (D251).
 *
 * Monté dans useAppState, au-dessus des trois shells : le sélecteur de langue
 * n'a pas à connaître le compte.
 * - Connexion (ou session restaurée) sur un navigateur sans choix local :
 *   l'interface prend la langue du compte. Un choix local n'est jamais écrasé.
 * - Changement de langue pendant que le compte est connecté : un seul appel
 *   pour l'enregistrer. Un échec ne gêne pas l'interface (journalisé).
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  normalizeLanguage, currentLanguage, hasLocalLanguageChoice,
  getKnownAccountLanguage, setKnownAccountLanguage,
} from './accountLanguage';
import { updateLanguage } from '../settings/profileService';

export default function useAccountLanguage(currentUser) {
  const { i18n } = useTranslation();
  const userId = currentUser?.id || currentUser?._id || null;
  const accountLanguage = normalizeLanguage(currentUser?.language);

  useEffect(() => {
    setKnownAccountLanguage(userId ? accountLanguage : null);
    if (!userId || !accountLanguage || hasLocalLanguageChoice()) return;
    if (currentLanguage(i18n) !== accountLanguage) i18n.changeLanguage(accountLanguage);
  }, [userId, accountLanguage, i18n]);

  useEffect(() => {
    if (!userId) return undefined;
    const onLanguageChanged = (lng) => {
      const language = normalizeLanguage(String(lng || '').slice(0, 2));
      if (!language || language === getKnownAccountLanguage()) return;
      setKnownAccountLanguage(language);
      updateLanguage(language).catch((err) => {
        // Non bloquant : l'interface garde la langue choisie
        console.error('saveAccountLanguage:', err);
      });
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => i18n.off('languageChanged', onLanguageChanged);
  }, [userId, i18n]);
}
